import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import toast from 'react-hot-toast';
import {
  useUpdateProjectMutation,
  useGetAllCategoriesQuery,
  useAddProjectImagesMutation,
  useAddProjectDocumentsMutation,
  useDeleteProjectImageMutation,
  useDeleteProjectDocumentMutation
} from '../../store/slices/apiSlice';
import { X, MapPin, CheckCircle, Image as ImageIcon, FileText, Upload, Plus } from 'lucide-react';
import { API_BASE_URL } from '../../config/api';

// Validation schema with user-friendly error messages
const projectSchema = yup.object({
  title: yup.string().required('This field is required'),
  short_description: yup.string().required('This field is required'),
  full_description: yup.string().required('This field is required'),
  category_id: yup.number().transform((_value, originalValue) => {
    if (originalValue === '' || originalValue === null || originalValue === undefined) return undefined;
    return Number(originalValue);
  }).required('Please select a category').min(1, 'Please select a valid category'),
  location: yup.string().required('This field is required'),
  funding_goal: yup.number().transform((_value, originalValue) => {
    if (originalValue === '' || originalValue === null || originalValue === undefined) return undefined;
    return Number(originalValue);
  }).required('This field is required').min(1, 'Funding goal must be greater than 0'),
  deadline: yup.string().required('This field is required').test('future-date', 'Deadline must be a future date', function(value) {
    if (!value) return true; // Let required() handle empty values
    const selectedDate = new Date(value);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to compare dates only
    return selectedDate > today;
  }),
  start_date: yup.string().optional(),
  expected_roi_percentage: yup.number().transform((_value, originalValue) => {
    if (originalValue === '' || originalValue === null || originalValue === undefined) return undefined;
    return Number(originalValue);
  }).required('This field is required').min(0, 'ROI percentage must be 0 or greater').max(100, 'ROI percentage cannot exceed 100%'),
  roi_duration_months: yup.number().transform((_value, originalValue) => {
    if (originalValue === '' || originalValue === null || originalValue === undefined) return undefined;
    return Number(originalValue);
  }).required('This field is required').min(1, 'Duration must be at least 1 month'),
  currency_code: yup.string().required('Please select a currency'),
  rules: yup.object({
    auto_close_on_goal: yup.boolean().required(),
    min_allowed_investment: yup.number().transform((_value, originalValue) => {
      if (originalValue === '' || originalValue === null || originalValue === undefined) return undefined;
      return Number(originalValue);
    }).required('This field is required').min(1, 'Minimum investment must be greater than 0'),
    max_allowed_investment: yup.number().transform((_value, originalValue) => {
      if (originalValue === '' || originalValue === null || originalValue === undefined) return undefined;
      return Number(originalValue);
    }).required('This field is required').min(1, 'Maximum investment must be greater than 0'),
  }).required(),
  status: yup.string().required('Please select a status'),
});

const ProjectUpdate = () => {
  const { state } = useLocation();
  const project = state?.project;
  const navigate = useNavigate();
  const [updateProject, { isLoading }] = useUpdateProjectMutation();
  const { data: categoriesResponse } = useGetAllCategoriesQuery();
  const categories = categoriesResponse?.data || [];

  // Tabs
  const [activeTab, setActiveTab] = useState('edit');

  // Location search state
  const [locationSearch, setLocationSearch] = useState(project?.location || '');
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const [locationSuggestions, setLocationSuggestions] = useState<any[]>([]);
  const [isSearchingLocation, setIsSearchingLocation] = useState(false);
  const [selectedLatitude, setSelectedLatitude] = useState(project?.latitude || null);
  const [selectedLongitude, setSelectedLongitude] = useState(project?.longitude || null);

  // Images/Documents state
  const [selectedImages, setSelectedImages] = useState<any[]>([]);
  const [selectedDocuments, setSelectedDocuments] = useState<any[]>([]);
  const [images, setImages] = useState(project?.images || []);
  const [documents, setDocuments] = useState(project?.documents || []);
  const [isAddingImages, setIsAddingImages] = useState(false);
  const [isAddingDocuments, setIsAddingDocuments] = useState(false);
  const [isDeletingImage, setIsDeletingImage] = useState(null);
  const [isDeletingDocument, setIsDeletingDocument] = useState(null);

  const [addImages] = useAddProjectImagesMutation();
  const [addDocuments] = useAddProjectDocumentsMutation();
  const [deleteProjectImage] = useDeleteProjectImageMutation();
  const [deleteProjectDocument] = useDeleteProjectDocumentMutation();

  // Add state for document name input
  const [documentNameInput, setDocumentNameInput] = useState('');

  // Pre-fill form with project data
  const { register, handleSubmit, formState: { errors, isValid }, setValue, reset } = useForm({
    resolver: yupResolver(projectSchema),
    mode: 'onChange',
    defaultValues: {
      ...project,
      category_id: project?.category?.category_id ? String(project.category.category_id) : (project?.category_id ? String(project.category_id) : ''),
      rules: {
        min_allowed_investment: project?.rules?.min_allowed_investment || '',
        max_allowed_investment: project?.rules?.max_allowed_investment || '',
        auto_close_on_goal: project?.rules?.auto_close_on_goal || false,
      },
      deadline: project?.deadline ? project.deadline.split('T')[0] : '',
      start_date: project?.start_date ? project.start_date.split('T')[0] : '',
      expected_roi_percentage: project?.expected_roi_percentage || '',
      roi_duration_months: project?.roi_duration_months || '',
      currency_code: project?.country_code || project?.currency_code || '',
    }
  });

  useEffect(() => {
    if (project) {
      const categoryId = project?.category?.category_id || project?.category_id;
      reset({
        ...project,
        category_id: categoryId ? String(categoryId) : '',
        rules: {
          min_allowed_investment: project?.rules?.min_allowed_investment || '',
          max_allowed_investment: project?.rules?.max_allowed_investment || '',
          auto_close_on_goal: project?.rules?.auto_close_on_goal || false,
        },
        deadline: project?.deadline ? project.deadline.split('T')[0] : '',
        start_date: project?.start_date ? project.start_date.split('T')[0] : '',
        expected_roi_percentage: project?.expected_roi_percentage || '',
        roi_duration_months: project?.roi_duration_months || '',
        currency_code: project?.country_code || project?.currency_code || '',
      });
      setImages(project.images || []);
      setDocuments(project.documents || []);
    }
  }, [project, reset]);

  // Set category_id after categories are loaded
  useEffect(() => {
    if (project && categories.length > 0) {
      const categoryId = project?.category?.category_id || project?.category_id;
      if (categoryId) {
        setValue('category_id', String(categoryId));
      }
    }
  }, [categories, project, setValue]);

  // Location search logic with enhanced authentication
  const searchLocation = async (query: string) => {
    if (!query.trim()) {
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);
      return;
    }

    // Check if user is authenticated
    const token = localStorage.getItem('diasporawise_admin_token');
    if (!token) {
      toast.error('You are not authenticated. Please login again.');
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);
      return;
    }

    // Validate token format
    if (token.length < 10) {
      toast.error('Invalid authentication token. Please login again.');
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);
      return;
    }

    // Check if token looks like a JWT (has dots)
    if (!token.includes('.')) {
      toast.error('Invalid token format. Please login again.');
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);
      return;
    }

    console.log('Searching location with token:', token.substring(0, 20) + '...');

    setIsSearchingLocation(true);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/search-location?input=${encodeURIComponent(query)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();

      if (data.success && data.data) {
        setLocationSuggestions(data.data);
        setShowLocationSuggestions(true);
      } else {
        setLocationSuggestions([]);
        setShowLocationSuggestions(false);
        if (data.message) {
          toast.error(`Location search failed: ${data.message}`);
        }
      }
    } catch (error: any) {
      console.error('Location search error:', error);
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);

      // Handle specific error cases
      if (error?.status === 401) {
        toast.error('Authentication failed. Please login again.');
      } else if (error?.status === 500) {
        toast.error('Server error. Please try again later.');
      } else {
        toast.error('Location search failed. Please try again.');
      }
    } finally {
      setIsSearchingLocation(false);
    }
  };

  const getLatLong = async (address: string) => {
    // Check if user is authenticated
    const token = localStorage.getItem('diasporawise_admin_token');
    if (!token) {
      toast.error('You are not authenticated. Please login again.');
      return null;
    }

    // Validate token format
    if (token.length < 10) {
      toast.error('Invalid authentication token. Please login again.');
      return null;
    }

    // Check if token looks like a JWT (has dots)
    if (!token.includes('.')) {
      toast.error('Invalid token format. Please login again.');
      return null;
    }

    console.log('Getting lat/long with token:', token.substring(0, 20) + '...');

    try {
      const response = await fetch(`${API_BASE_URL}/admin/get-lat-long?address=${encodeURIComponent(address)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success && data.data) {
        // Handle both string format (comma-separated) and object format
        let latitude: number, longitude: number;

        if (typeof data.data === 'string') {
          const [lat, lng] = data.data.split(',').map(Number);
          latitude = lat;
          longitude = lng;
        } else if (typeof data.data === 'object' && data.data.latitude && data.data.longitude) {
          latitude = data.data.latitude;
          longitude = data.data.longitude;
        } else {
          console.error('Unexpected data format:', data.data);
          return null;
        }

        return { latitude, longitude };
      } else {
        console.error('API returned error:', data.message);
        toast.error(`Failed to get coordinates: ${data.message || 'Unknown error'}`);
        return null;
      }
    } catch (error: any) {
      console.error('Get lat/long error:', error);

      // Handle specific error cases
      if (error?.status === 401) {
        toast.error('Authentication failed. Please login again.');
      } else if (error?.status === 500) {
        toast.error('Server error. Please try again later.');
      } else {
        toast.error('Failed to get coordinates. Please try again.');
      }

      return null;
    }
  };

  const handleLocationSearch = (value: string) => {
    setLocationSearch(value);
    setValue('location', value);
    const timeoutId = setTimeout(() => {
      searchLocation(value);
    }, 300);
    return () => clearTimeout(timeoutId);
  };

  const selectLocation = async (location: any) => {
    setLocationSearch(location.description);
    setValue('location', location.description);
    setShowLocationSuggestions(false);
    const latLong = await getLatLong(location.description);
    if (latLong) {
      setSelectedLatitude(latLong.latitude);
      setSelectedLongitude(latLong.longitude);
    }
  };

  // Image upload logic
  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const imageFiles = files.filter((file: any) => file.type.startsWith('image/'));
    if (imageFiles.length + selectedImages.length > 10) return;
    setSelectedImages((prev: any[]) => [...prev, ...imageFiles]);
  };
  const removeImage = (index: number) => {
    setSelectedImages((prev: any[]) => prev.filter((_: any, i: number) => i !== index));
  };
  const handleAddImages = async () => {
    if (selectedImages.length === 0) return;
    setIsAddingImages(true);
    try {
      const result = await addImages({ project_id: project.project_id, images: selectedImages }).unwrap();
      
      // Safety check (though baseQuery now handles success: false as errors)
      if (result && result.success === false) {
        toast.error(result.message || 'Failed to upload images');
        return;
      }
      
      toast.success('Images uploaded successfully!');
      setSelectedImages([]);
      // Refetch or update images state
      setImages((prev: any[]) => [...prev, ...selectedImages.map(file => ({ image_url: URL.createObjectURL(file) }))]);
    } catch (error: any) {
      console.error('Image upload error:', error);
      const errorMessage = error?.data?.message || error?.message || 'Failed to upload images';
      toast.error(errorMessage);
    } finally {
      setIsAddingImages(false);
    }
  };
  const handleDeleteImage = async (imageId: number) => {
    setIsDeletingImage(imageId as any);
    try {
      await deleteProjectImage({ image_id: imageId, project_id: project.project_id }).unwrap();
      toast.success('Image deleted');
      setImages((prev: any[]) => prev.filter((img: any) => img.project_images_id !== imageId));
    } catch (e) {
      toast.error('Failed to delete image');
    } finally {
      setIsDeletingImage(null as any);
    }
  };

  // Document upload logic
  const handleDocumentUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const documentFiles = files.filter((file: any) => file.type === 'application/pdf' || file.type.includes('document') || file.type.includes('text'));
    if (documentFiles.length + selectedDocuments.length > 5) return;
    const newDocuments = documentFiles.map((file: any) => ({ name: file.name, file }));
    setSelectedDocuments((prev: any[]) => [...prev, ...newDocuments]);
  };
  const removeDocument = (index: number) => {
    setSelectedDocuments((prev: any[]) => prev.filter((_: any, i: number) => i !== index));
  };
  const handleAddDocuments = async () => {
    if (selectedDocuments.length === 0) return;
    if (!documentNameInput.trim()) {
      toast.error('Please enter a document name');
      return;
    }
    setIsAddingDocuments(true);
    try {
      for (const doc of selectedDocuments) {
        await addDocuments({ project_id: project.project_id, document_name: documentNameInput, document: doc.file }).unwrap();
      }
      toast.success('Documents uploaded successfully!');
      setSelectedDocuments([]);
      setDocumentNameInput('');
      setDocuments((prev: any[]) => [...prev, ...selectedDocuments.map(_doc => ({ document_name: documentNameInput }))]);
    } catch (e) {
      toast.error('Failed to upload documents');
    } finally {
      setIsAddingDocuments(false);
    }
  };
  const handleDeleteDocument = async (docId: number) => {
    setIsDeletingDocument(docId as any);
    try {
      await deleteProjectDocument({ document_id: docId, project_id: project.project_id }).unwrap();
      toast.success('Document deleted');
      setDocuments((prev: any[]) => prev.filter((doc: any) => doc.project_documents_id !== docId));
    } catch (e) {
      toast.error('Failed to delete document');
    } finally {
      setIsDeletingDocument(null as any);
    }
  };

  const onSubmit = async (data: any) => {
    console.log(data);
    if (!selectedLatitude || !selectedLongitude) {
      toast.error('Please select a valid location from the suggestions. Type in the location field and choose from the dropdown.');
      return;
    }

    // Check if user is authenticated
    const token = localStorage.getItem('diasporawise_admin_token');
    if (!token) {
      toast.error('You are not authenticated. Please login again.');
      return;
    }

    // Validate token format
    if (token.length < 10) {
      toast.error('Invalid authentication token. Please login again.');
      return;
    }

    // Check if token looks like a JWT (has dots)
    if (!token.includes('.')) {
      toast.error('Invalid token format. Please login again.');
      return;
    }

    console.log('Updating project with token:', token.substring(0, 20) + '...');

    try {
      const payload = {
        project_id: project.project_id,
        title: data.title,
        short_description: data.short_description,
        full_description: data.full_description,
        category_id: data.category_id,
        location: data.location,
        latitude: selectedLatitude,
        longitude: selectedLongitude,
        funding_goal: data.funding_goal,
        deadline: data.deadline ? new Date(data.deadline).toISOString() : undefined,
        start_date: data.start_date ? new Date(data.start_date).toISOString() : undefined,
        expected_roi_percentage: data.expected_roi_percentage,
        roi_duration_months: data.roi_duration_months,
        currency_code: data.currency_code,
        rules: {
          auto_close_on_goal: data.rules?.auto_close_on_goal,
          min_allowed_investment: data.rules?.min_allowed_investment,
          max_allowed_investment: data.rules?.max_allowed_investment,
        },
        status: data.status,
      };
      console.log(payload);
      const res = await updateProject(payload).unwrap();
      if (res.success) {
        toast.success((res?.message as string) || 'Project updated successfully!');
        navigate('/admin/projects');
      } else {
        toast.error((res?.message as string) || 'Update failed');
      }
    } catch (e: any) {
      console.error('Update project error:', e);

      // Handle specific error cases
      if (e?.status === 400) {
        toast.error('Invalid request format. Please check all fields and try again.');
      } else if (e?.status === 401) {
        toast.error('Authentication failed. Please login again.');
      } else if (e?.status === 404) {
        toast.error('Project not found. Please refresh and try again.');
      } else if (e?.status === 500) {
        toast.error('Server error. Please try again later.');
      } else {
        toast.error(e?.data?.message || 'Update failed. Please try again.');
      }
    }
  };

  if (!project) return <div>No project data</div>;

  // Tab Button
  interface TabButtonProps {
    icon: React.ReactNode;
    label: string;
    isActive: boolean;
    onClick: () => void;
  }
  const TabButton = ({ icon, label, isActive, onClick }: TabButtonProps) => (
    <button
      onClick={onClick}
      className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-colors ${isActive ? 'bg-blue-600 text-white' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
    >
      {icon}
      <span>{label}</span>
    </button>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto py-8 px-4">
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Update Project</h1>
              <p className="text-gray-600 mt-1">Edit your project details, images, and documents</p>
            </div>
            <button
              onClick={() => navigate('/admin/projects')}
              className="flex items-center space-x-2 py-2 px-4 text-gray-600 hover:text-gray-800 transition-colors"
            >
              <X className="w-4 h-4" />
              <span>Back to Projects</span>
            </button>
          </div>
          <div className="mt-6 flex space-x-2">
            <TabButton icon={<Plus className="w-4 h-4" />} label="Edit Project" isActive={activeTab === 'edit'} onClick={() => setActiveTab('edit')} />
            <TabButton icon={<ImageIcon className="w-4 h-4" />} label="Images" isActive={activeTab === 'images'} onClick={() => setActiveTab('images')} />
            <TabButton icon={<FileText className="w-4 h-4" />} label="Documents" isActive={activeTab === 'documents'} onClick={() => setActiveTab('documents')} />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm">
          {activeTab === 'edit' && (
            <form onSubmit={handleSubmit(onSubmit)} className="p-6">
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Project Title *</label>
                    <input
                      type="text"
                      {...register('title')}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter project title"
                    />
                    {errors.title && (
                      <p className="text-red-500 text-sm mt-1">{String(errors.title.message)}</p>
                    )}
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Location *</label>
                    <div className="relative">
                      <input
                        type="text"
                        value={locationSearch}
                        onChange={(e) => handleLocationSearch(e.target.value)}
                        className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Search for location"
                      />
                      <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      {isSearchingLocation && (
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        </div>
                      )}
                    </div>
                    {showLocationSuggestions && locationSuggestions.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {locationSuggestions.map((location) => (
                          <button
                            key={location.place_id}
                            type="button"
                            onClick={() => selectLocation(location)}
                            className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center space-x-2"
                          >
                            <MapPin className="w-4 h-4 text-gray-400" />
                            <div className="text-left">
                              <div className="font-medium">{location.structured_formatting.main_text}</div>
                              <div className="text-sm text-gray-500">{location.structured_formatting.secondary_text}</div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    {selectedLatitude && selectedLongitude && (
                      <p className="text-green-600 text-sm mt-1">
                        ✓ Location coordinates: {selectedLatitude.toFixed(6)}, {selectedLongitude.toFixed(6)}
                      </p>
                    )}
                    {errors.location && (
                      <p className="text-red-500 text-sm mt-1">{String(errors.location.message)}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
                    <select
                      {...register('category_id')}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select category</option>
                      {categories.map((category) => (
                        <option key={category.category_id} value={String(category.category_id)}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                    {errors.category_id && (
                      <p className="text-red-500 text-sm mt-1">{String(errors.category_id.message)}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Deadline</label>
                    <input
                      type="date"
                      {...register('deadline')}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    {errors.deadline && (
                      <p className="text-red-500 text-sm mt-1">{String(errors.deadline.message)}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Start Date (Optional)</label>
                    <input
                      type="date"
                      {...register('start_date')}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    {errors.start_date && (
                      <p className="text-red-500 text-sm mt-1">{String(errors.start_date.message)}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Funding Goal ($) *</label>
                    <input
                      type="number"
                      {...register('funding_goal')}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter funding goal"
                    />
                    {errors.funding_goal && (
                      <p className="text-red-500 text-sm mt-1">{String(errors.funding_goal.message)}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Minimum Investment ($) *</label>
                    <input
                      type="number"
                      {...register('rules.min_allowed_investment')}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter minimum investment"
                    />
                    {errors.rules && (errors.rules as any).min_allowed_investment && (
                      <p className="text-red-500 text-sm mt-1">{String((errors.rules as any).min_allowed_investment.message)}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Maximum Investment ($) *</label>
                    <input
                      type="number"
                      {...register('rules.max_allowed_investment')}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter maximum investment"
                    />
                    {errors.rules && (errors.rules as any).max_allowed_investment && (
                      <p className="text-red-500 text-sm mt-1">{String((errors.rules as any).max_allowed_investment.message)}</p>
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        {...register('rules.auto_close_on_goal')}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        Auto-close project when funding goal is reached
                      </span>
                    </label>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Project Status *</label>
                    <select
                      {...register('status')}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select status</option>
                      <option value="draft">Draft</option>
                      <option value="active">Active</option>
                      <option value="closed">Closed</option>
                    </select>
                    {errors.status && (
                      <p className="text-red-500 text-sm mt-1">{String(errors.status.message)}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Expected ROI Percentage (%) *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      {...register('expected_roi_percentage')}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter expected ROI percentage"
                    />
                    {errors.expected_roi_percentage && (
                      <p className="text-red-500 text-sm mt-1">{String(errors.expected_roi_percentage.message)}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      ROI Duration (Months) *
                    </label>
                    <input
                      type="number"
                      {...register('roi_duration_months')}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter ROI duration in months"
                    />
                    {errors.roi_duration_months && (
                      <p className="text-red-500 text-sm mt-1">{String(errors.roi_duration_months.message)}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Currency Code *
                    </label>
                    <select
                      {...register('currency_code')}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select currency</option>
                      <option value="USD">USD - US Dollar</option>
                      <option value="EUR">EUR - Euro</option>
                      <option value="GBP">GBP - British Pound</option>
                      <option value="INR">INR - Indian Rupee</option>
                      <option value="NGN">NGN - Nigerian Naira</option>
                      <option value="CAD">CAD - Canadian Dollar</option>
                      <option value="AUD">AUD - Australian Dollar</option>
                      <option value="JPY">JPY - Japanese Yen</option>
                      <option value="CNY">CNY - Chinese Yuan</option>
                      <option value="AED">AED - UAE Dirham</option>
                    </select>
                    {errors.currency_code && (
                      <p className="text-red-500 text-sm mt-1">{String(errors.currency_code.message)}</p>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Short Description *</label>
                  <textarea
                    {...register('short_description')}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter short description"
                  />
                  {errors.short_description && (
                    <p className="text-red-500 text-sm mt-1">{String(errors.short_description.message)}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Full Description *</label>
                  <textarea
                    {...register('full_description')}
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter detailed description"
                  />
                  {errors.full_description && (
                    <p className="text-red-500 text-sm mt-1">{String(errors.full_description.message)}</p>
                  )}
                </div>
                <div className="flex justify-end pt-6 border-t border-gray-200">
                  <button
                    type="submit"
                    disabled={!isValid || isLoading}
                    className="flex items-center space-x-2 py-3 px-6 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                  >
                    {isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Updating...</span>
                      </>
                    ) : (
                      <>
                        <span>Update Project</span>
                        <CheckCircle className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          )}
          {activeTab === 'images' && (
            <div className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Project Images</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {images.map((img: any) => (
                    <div key={img.project_images_id} className="relative group">
                      <img src={img.image_url} alt="" className="w-full h-24 object-cover rounded-lg" />
                      <button
                        type="button"
                        onClick={() => handleDeleteImage(img.project_images_id)}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        disabled={isDeletingImage === img.project_images_id}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Add Images (Max 10)</label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    id="image-upload"
                  />
                  <label htmlFor="image-upload" className="cursor-pointer">
                    <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-600">Click to upload images or drag and drop</p>
                    <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB each</p>
                  </label>
                </div>
                {selectedImages.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Selected Images ({selectedImages.length})</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {selectedImages.map((image, index) => (
                        <div key={index} className="relative group">
                          <img src={URL.createObjectURL(image)} alt={`Preview ${index + 1}`} className="w-full h-24 object-cover rounded-lg" />
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3" />
                          </button>
                          <p className="text-xs text-gray-500 mt-1 truncate">{image.name}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex justify-end pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleAddImages}
                    disabled={selectedImages.length === 0 || isAddingImages}
                    className="flex items-center space-x-2 py-3 px-6 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                  >
                    {isAddingImages ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Uploading...</span>
                      </>
                    ) : (
                      <>
                        <span>Upload Images</span>
                        <Upload className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
          {activeTab === 'documents' && (
            <div className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Document Name *</label>
                <input
                  type="text"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={documentNameInput}
                  onChange={e => setDocumentNameInput(e.target.value)}
                  placeholder="Enter document name"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Project Documents</label>
                <div className="space-y-2">
                  {documents.map((doc: any) => (
                    <div key={doc.project_documents_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <FileText className="w-5 h-5 text-gray-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{doc.document_name}</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDeleteDocument(doc.project_documents_id)}
                        className="text-red-500 hover:text-red-700"
                        disabled={isDeletingDocument === doc.project_documents_id}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Add Documents (Max 5)</label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.txt"
                    onChange={handleDocumentUpload}
                    className="hidden"
                    id="document-upload"
                  />
                  <label htmlFor="document-upload" className="cursor-pointer">
                    <FileText className="mx-auto h-12 w-12 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-600">Click to upload documents or drag and drop</p>
                    <p className="text-xs text-gray-500">PDF, DOC, DOCX, TXT up to 10MB each</p>
                  </label>
                </div>
                {selectedDocuments.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Selected Documents ({selectedDocuments.length})</h4>
                    <div className="space-y-2">
                      {selectedDocuments.map((document, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <FileText className="w-5 h-5 text-gray-400" />
                            <div>
                              <p className="text-sm font-medium text-gray-900">{document.name}</p>
                              <p className="text-xs text-gray-500">{(document.file.size / 1024 / 1024).toFixed(2)} MB</p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeDocument(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex justify-end pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleAddDocuments}
                    disabled={selectedDocuments.length === 0 || isAddingDocuments}
                    className="flex items-center space-x-2 py-3 px-6 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                  >
                    {isAddingDocuments ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Uploading...</span>
                      </>
                    ) : (
                      <>
                        <span>Upload Documents</span>
                        <Upload className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectUpdate;